import { useCallback, useEffect, useState } from "preact/hooks";
import type { Article, SortKey } from "../lib/types";
import {
  listArticles,
  archiveArticle,
  favoriteArticle,
  deleteArticles,
  bulkArchive,
  addTagsToArticle,
} from "../lib/db";
import { navigate } from "../lib/router";
import { ArticleList } from "../components/ArticleList";
import { CaptureModal } from "../components/CaptureModal";
import { consumeShareTarget } from "../lib/share-target";

interface InboxProps {
  params: Record<string, string>;
}

const SORTS: { key: SortKey; label: string }[] = [
  { key: "saved", label: "Saved" },
  { key: "published", label: "Published" },
  { key: "title", label: "Title" },
];

export function Inbox({ params }: InboxProps) {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<SortKey>("saved");
  const [search, setSearch] = useState("");
  const [capture, setCapture] = useState(Boolean(params.capture));
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setArticles(await listArticles({ filter: "inbox", sort, search: search || undefined }));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [sort, search]);

  useEffect(() => { void reload(); }, [reload]);

  // Pull a pending share_target URL from the query string (Android share menu).
  useEffect(() => {
    void consumeShareTarget().then((shared) => {
      if (shared) setCapture(true);
    });
  }, []);

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const onArchive = async (id: string) => {
    await archiveArticle(id, true);
    setArticles((prev) => prev.filter((a) => a.id !== id));
  };

  const onFavorite = async (id: string, value: boolean) => {
    await favoriteArticle(id, value);
    setArticles((prev) => prev.map((a) => (a.id === id ? { ...a, favorite: value } : a)));
  };

  const onBulkArchive = async () => {
    if (!selected.size) return;
    await bulkArchive([...selected], true);
    setSelected(new Set());
    await reload();
  };

  const onBulkDelete = async () => {
    if (!selected.size) return;
    if (!confirm(`Delete ${selected.size} article(s)? This cannot be undone.`)) return;
    await deleteArticles([...selected]);
    setSelected(new Set());
    await reload();
  };

  const onBulkTag = async () => {
    const names = prompt("Add tags (comma-separated):")?.split(",").map((s) => s.trim()).filter(Boolean);
    if (!names?.length) return;
    for (const id of selected) await addTagsToArticle(id, names);
    setSelected(new Set());
  };

  return (
    <>
      <div class="topbar">
        <h1>Inbox</h1>
        <div class="grow" />
        <select value={sort} onChange={(e) => setSort((e.target as HTMLSelectElement).value as SortKey)}
          aria-label="Sort by">
          {SORTS.map((s) => <option value={s.key}>{s.label}</option>)}
        </select>
        <input
          type="search"
          placeholder="Search inbox…"
          value={search}
          onInput={(e) => setSearch((e.target as HTMLInputElement).value)}
          aria-label="Search inbox"
          style="min-width: 12rem;"
        />
        <button class="primary" onClick={() => setCapture(true)}>+ Add URL</button>
      </div>

      {selected.size > 0 && (
        <div class="banner" style="display: flex; gap: var(--space-3); align-items: center;">
          <strong>{selected.size} selected</strong>
          <div class="grow" />
          <button onClick={onBulkArchive}>Archive</button>
          <button onClick={onBulkTag}>Tag</button>
          <button onClick={onBulkDelete} style="color: var(--danger);">Delete</button>
          <button class="ghost" onClick={() => setSelected(new Set())}>Clear</button>
        </div>
      )}

      {error && <div class="banner error">{error}</div>}

      {loading ? (
        <div class="empty"><div class="spinner" /></div>
      ) : articles.length === 0 ? (
        <div class="empty">
          <h2>Inbox is empty</h2>
          <p class="muted">Save a URL to start reading.</p>
          <button class="primary" onClick={() => setCapture(true)}>+ Add URL</button>
        </div>
      ) : (
        <ArticleList
          articles={articles}
          selected={selected}
          onToggleSelect={toggleSelect}
          onArchive={onArchive}
          onFavorite={onFavorite}
          onOpen={(a) => navigate(`/articles/${a.id}`)}
        />
      )}

      {capture && (
        <CaptureModal
          onClose={() => setCapture(false)}
          onSaved={(id) => { setCapture(false); navigate(`/articles/${id}`); }}
        />
      )}
    </>
  );
}
